import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Col, Row, Table } from 'reactstrap';

import {
  Column,
  Table as ReactTable,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

import { Pagination } from 'Components/pos2u/Pagination';

import { useTranslation } from 'react-i18next';
import useWindowSize from 'Components/Hooks/WindowSizeHook';

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        // value={(columnFilterValue ?? '') as string}
        value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns: ColumnDef<any, any>[];
  data?: any;
  customPageSize: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  tdClass?: string;
  divClass?: string;
  SearchPlaceholder?: string;
  selectedCheckBoxDelete?: number;
  TotalData?: number;
  totalPages: number;
  currentPage: number;
  setEntriesPerPage: (value: number) => void;
  handlePageChange: (value: number) => void;
  handleSearchFiltersSubmit?: (value: any) => void;
  initialFilters?: any;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  itemsPerPage?: number[];
  columnWhiteSpace?: string;
}

const InventoryHistoryTable = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass = '',
  tdClass = '',
  divClass,
  // TotalData,
  totalPages,
  currentPage,

  handlePageChange,
  inputPageValue,
  setInputPageValue,
  columnWhiteSpace = '',
}: TableContainerProps) => {
  // const layoutState = useSelector((state: RootState) => state.Layout);
  // const { layoutModeType } = layoutState;
  // const isDarkMode = layoutModeType === 'dark';
  const { t } = useTranslation();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns: columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    // getCanPreviousPage,
    // getCanNextPage,
    // getPageOptions,
    // setPageIndex,
    // nextPage,
    // previousPage,
    setPageSize,
    // getState,
  } = table;

  useEffect(() => {
    if (Number(customPageSize)) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0) {
      handlePageChange(currentPage - 1);
    }
    setInputPageValue(currentPage);
  }, [currentPage, handlePageChange, setInputPageValue]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      handlePageChange(currentPage + 1);
      setInputPageValue(currentPage + 2);
    }
  }, [totalPages, currentPage, handlePageChange, setInputPageValue]);

  return (
    <Fragment>
      <Row className="mb-3 align-items-center g-3 text-center text-sm-start px-1 mx-auto">
        <Col lg={4} xs={12} className="px-0 mx-0"></Col>
        <Col lg={8} xs={12} className="px-0 mx-0">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            inputPageValue={inputPageValue}
            setInputPageValue={setInputPageValue}
            showInputPageValue={false}
          />
        </Col>
      </Row>

      <div className={divClass}>
        <Table
          hover
          className={tableClass}
          // style={{
          //   // tableLayout: 'fixed',
          //   width: '100%',
          // }}
          style={{
            tableLayout: 'fixed',
            width: '100%',
            wordWrap: 'break-word',
          }}
        >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    style={{
                      fontSize: isMobile ? 'small' : '',
                      whiteSpace: columnWhiteSpace,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      ...header.column.columnDef.meta?.style,
                    }}
                    // style={{
                    //   // overflow: 'hidden',
                    //   // textOverflow: 'ellipsis',
                    //   // whiteSpace: 'nowrap',
                    //   ...header.column.columnDef.meta?.style,
                    // }}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' '
                          : header.column.getIsSorted() === 'desc'
                          ? ' '
                          : null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={getHeaderGroups()[0].headers.length}>
                  <div className="text-muted text-center">
                    <span>{t('No data available in table')}</span>
                  </div>
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row: any) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell: any) => {
                      return (
                        <td
                          className={tdClass}
                          key={cell.id}
                          style={{
                            fontSize: isMobile ? 'small' : '',
                            whiteSpace: columnWhiteSpace,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            ...cell.column.columnDef.meta?.cellStyle,
                          }}
                          // style={{
                          //   // overflow: 'hidden',
                          //   // textOverflow: 'ellipsis',
                          //   // whiteSpace: 'nowrap',
                          //   ...cell.column.columnDef.meta?.cellStyle,
                          // }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <Row className="mb-3 align-items-center g-3 text-center text-sm-start px-1 mx-auto">
        <Col lg={4} xs={12} className="px-0 mx-0"></Col>
        <Col lg={8} xs={12} className="px-0 mx-0">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            inputPageValue={inputPageValue}
            setInputPageValue={setInputPageValue}
            showInputPageValue={false}
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export default InventoryHistoryTable;
